import {
  Nullable,
  SearchRelevancyGrade,
  UserRelation,
} from '@aibees/shared/definition';
import { CompanyFundingType } from '../search-filters/config/funding-types';

export interface SearchTableCompanyProps {
  className?: string;
}

export enum COMPANY_TYPE {
  PUBLIC_COMPANY = 'PUBLIC_COMPANY',
  PRIVATELY_HELD = 'PRIVATELY_HELD',
  PARTNERSHIP = 'PARTNERSHIP',
  GOVERNMENT_AGENCY = 'GOVERNMENT_AGENCY',
  NON_PROFIT = 'NON_PROFIT',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  EDUCATIONAL = 'EDUCATIONAL',
  SELF_OWNED = 'SELF_OWNED',
}

export interface CompanyLogo {
  source: Nullable<string>;
  uploaded: Nullable<boolean>;
}

export interface CompanyStaffRange {
  start: Nullable<number>;
  end: Nullable<number>;
}

export interface CompanyStaff {
  total: Nullable<number>;
  range: Nullable<CompanyStaffRange>;
}

export interface CompanySummary {
  name: string;
  legal_name: Nullable<string>;
  description: Nullable<string>;
  overview: Nullable<string>;
  seo: Nullable<string>;
  founded_year: Nullable<number>;
  type: Nullable<COMPANY_TYPE>;
  industry: Nullable<string>;
  staff: Nullable<CompanyStaff>;
  logo: Nullable<CompanyLogo>;
  sic_code: Nullable<string>;
}

export interface CompanyLink {
  twitter: Nullable<string>;
  facebook: Nullable<string>;
  instagram: Nullable<string>;
  linkedin: Nullable<string>;
  youtube: Nullable<string>;
  angellist: Nullable<string>;
  crunchbase: Nullable<string>;
  blog: Nullable<string>;
  website: Nullable<string>;
  domain: Nullable<string>;
  domain_ltd: Nullable<string>;
  domains: Nullable<string[]>;
  urls: Nullable<string[]>;
}

export interface GeoLocation {
  lng: number;
  lat: number;
}

export interface CompanyLocation {
  continent: Nullable<string>;
  country: Nullable<string>;
  state: Nullable<string>;
  city: Nullable<string>;
  street: Nullable<string>;
  postal_code: Nullable<string>;
  raw_address: Nullable<string>;
  position: Nullable<GeoLocation>;
}

export interface CompanyLocationsInfo {
  headquarter: Nullable<CompanyLocation>;
  locations: Nullable<CompanyLocation[]>;
}

export interface CompanyRetailLocation extends CompanyLocation {
  url: Nullable<string>;
  phone: Nullable<string>;
}

export interface CompanyRetailsInfo {
  total: Nullable<number>;
  locations: Nullable<CompanyRetailLocation[]>;
}

export interface CompanyRevenue {
  amount: Nullable<string>;
  duration: Nullable<string>;
  source: Nullable<string>;
}

export interface CompanyFundingRound {
  announced_at: Nullable<string>;
  raised_amount: Nullable<number>;
  type: Nullable<CompanyFundingType>;
  investors: Nullable<string[]>;
}

export interface CompanyFunding {
  type: Nullable<CompanyFundingType>;
  total_amount: Nullable<number>;
  last_amount: Nullable<number>;
  num_investor: Nullable<number>;
  num_funding_rounds: Nullable<number>;
  rounds: Nullable<CompanyFundingRound[]>;
  date: Nullable<string>; // this is the latest funding date
}

export interface CompanyIpoLink {
  label: Nullable<string>; // market symbol
  value: Nullable<string>;
}

export interface CompanyIPO {
  link: Nullable<CompanyIpoLink>;
  raised_amount: Nullable<number>;
  valuation: Nullable<number>; // market cap of the company
  share_price: Nullable<number>;
  date: Nullable<string>;
}

export interface CompanyFinancialIdentifier {
  id: Nullable<string>;
  name: Nullable<string>;
  image: Nullable<string>;
}

export interface CompanyFinancialExitItem {
  identifier: Nullable<CompanyFinancialIdentifier>;
  description: Nullable<string>;
}

export interface CompanyFinancialExit {
  total: Nullable<number>;
  contents: Nullable<CompanyFinancialExitItem[]>;
}

export interface CompanyFinancialAcquisitionItem {
  identifier: Nullable<CompanyFinancialIdentifier>;
  role: Nullable<string>;
  price: Nullable<number>;
  announced_at: Nullable<string>;
}

export interface CompanyFinancialAcquisition {
  total: Nullable<number>;
  contents: Nullable<CompanyFinancialAcquisitionItem[]>;
}

export interface CompanyInvestmentRound {
  identifier: Nullable<CompanyFinancialIdentifier>;
  type: Nullable<string>; // TODO: V2: get enums from the back-end
  raised_amount: Nullable<number>;
}

export interface CompanyFinancialInvestmentItem {
  identifier: Nullable<CompanyFinancialIdentifier>;
  is_lead: Nullable<boolean>;
  round: Nullable<CompanyInvestmentRound>;
}

export interface CompanyFinancialInvestment {
  total: Nullable<number>;
  leads: Nullable<number>;
  contents: Nullable<CompanyFinancialInvestmentItem[]>;
}

export interface CompanyFinancialDiversitytInvestment {
  total: Nullable<number>;
  contents: Nullable<CompanyFinancialInvestmentItem[]>;
}

export interface CompanyFinancialAberdeen {
  it_spend: Nullable<number>;
}

export interface CompanyFinancial {
  revenue: Nullable<{
    annual: Nullable<CompanyRevenue>;
    quarter: Nullable<CompanyRevenue>;
  }>;
  funding: Nullable<CompanyFunding>;
  ipo: Nullable<CompanyIPO>;
  exit: Nullable<CompanyFinancialExit>;
  acquisition: Nullable<CompanyFinancialAcquisition>;
  investment: Nullable<CompanyFinancialInvestment>;
  diversityInvestment: Nullable<CompanyFinancialDiversitytInvestment>;
  aberdeen: Nullable<CompanyFinancialAberdeen>;
}

export interface CompanySemrushLocation {
  continent: Nullable<string>;
  country: Nullable<string>;
  share_of_monthly_visits: Nullable<number>;
  monthly_visits_growth: Nullable<number>;
  rank: Nullable<number>;
  rank_growth: Nullable<number>;
}

export interface CompanySemrush {
  rank: Nullable<number>;
  visits_latest_month: Nullable<number>;
  monthly_visits_growth: Nullable<number>;
  updated_at: Nullable<string>;
  locations: Nullable<CompanySemrushLocation[]>;
}

export interface CompanyBomboraTopic {
  category: Nullable<string>;
  topic: Nullable<string>;
  score: Nullable<number>;
  growth: Nullable<number>;
  weeks: Nullable<number>;
}

export interface CompanyBombora {
  updated_at: Nullable<string>;
  topics: Nullable<CompanyBomboraTopic[]>;
}

export interface CompanyApptopiaAppInfo {
  name: Nullable<string>;
  image: Nullable<string>;
  stores: Nullable<string[]>;
  monthly_downloads: Nullable<number>;
}

export interface CompnayApptopia {
  total_apps: Nullable<number>;
  total_downloads: Nullable<number>;
  growth: Nullable<number>;
  apps: Nullable<CompanyApptopiaAppInfo[]>;
}

// these numbers are indicating `monthly` periods
export interface CompanyAnalyticsLinkedinGrowthMetrics {
  one: Nullable<number>;
  three: Nullable<number>;
  six: Nullable<number>;
  twelve: Nullable<number>;
  twenty_four: Nullable<number>;
}

export enum CompanyDepartments {
  program_and_project_management = 'program_and_project_management',
  education = 'education',
  purchasing = 'purchasing',
  accounting = 'accounting',
  sales = 'sales',
  research = 'research',
  operations = 'operations',
  customer_success_and_support = 'customer_success_and_support',
  legal = 'legal',
  product_management = 'product_management',
  media_and_communication = 'media_and_communication',
  real_estate = 'real_estate',
  quality_assurance = 'quality_assurance',
  consulting = 'consulting',
  arts_and_design = 'arts_and_design',
  healthcare_services = 'healthcare_services',
  entrepreneurship = 'entrepreneurship',
  engineering = 'engineering',
  community_and_social_services = 'community_and_social_services',
  marketing = 'marketing',
  information_technology = 'information_technology',
  business_development = 'business_development',
  administrative = 'administrative',
  military_and_protective_services = 'military_and_protective_services',
  finance = 'finance',
  human_resources = 'human_resources',
}

export type CompanyDepartmentKeys = CompanyDepartments | 'all';

export type CompanyAnalyticsLinkedinGrowth = {
  [key in CompanyDepartmentKeys]: Nullable<CompanyAnalyticsLinkedinGrowthMetrics>;
};

export interface CompanyAnalyticsLinkedin {
  metrics: Nullable<CompanyMetrics[]>;
  growth: Nullable<CompanyAnalyticsLinkedinGrowth>;
}

export interface CompanyAnalyticsIpqwery {
  trademark: Nullable<number>;
  patent: Nullable<number>;
}

export interface CompanyAnalytics {
  semrush: Nullable<CompanySemrush>;
  bombora: Nullable<CompanyBombora>;
  apptopia: Nullable<CompnayApptopia>;
  ipqwery: Nullable<CompanyAnalyticsIpqwery>;
  linkedin: Nullable<CompanyAnalyticsLinkedin>;
}

export interface CompanyPhone {
  raw: Nullable<string>;
  sanitized: Nullable<string>;
}

export interface CompanyContact {
  email: Nullable<string>;
  phone: Nullable<CompanyPhone>;
}

export interface Company {
  id: string;
  sic: Nullable<Array<string>>;
  naics: Nullable<Array<string>>;
  canSimilaritySearch?: boolean;
  summary: Nullable<CompanySummary>;
  contact?: Nullable<CompanyContact>;
  link: Nullable<CompanyLink>;
  location: Nullable<CompanyLocationsInfo>;
  retail?: Nullable<CompanyRetailsInfo>;
  financial?: Nullable<CompanyFinancial>;
  industries?: Nullable<string[]>;
  languages?: Nullable<string[]>;
  keywords?: Nullable<string[]>;
  technologies?: Nullable<CompanyTechnologies[]>;
  analytics?: Nullable<CompanyAnalytics>;
  labels?: Nullable<string[]>;
  user?: Nullable<UserRelation>;
  saved: boolean;
  relevancy?: Nullable<CompanyRelevancy>;
  hashtags?: Nullable<string[]>;
  sub_organizations?: Nullable<string[]>;
}

export type CompanyTableInfo = Omit<Company, 'analytics'>;

export interface CompanyRelevancy {
  score: number;
  value: SearchRelevancyGrade;
}

export interface CompanyTechnologies {
  name: Nullable<string>;
  category: Nullable<string>; // TODO: ask the back-end: do we have enums for this?
}

export interface CompanyMetrics {
  start_date: Nullable<string>;
  departments: CompanyMetricsDepartmentsItem[];
}

export type CompanyMetricsDepartmentsItem = CompanyMetricsDepartmentFunction &
  CompanyMetricsDepartmentData;

export type CompanyMetricsDepartmentFunction = {
  functions: Nullable<CompanyDepartmentKeys>;
};

export type CompanyMetricsDepartmentData = {
  retained: Nullable<number>;
  churned: Nullable<number>;
  new: Nullable<number>;
  monthly_percentage_difference: Nullable<number>;
};
